import { acceptHMRUpdate, defineStore } from 'pinia';
import type { AddToCartAttribute } from '@hypercodestudio/basler-components/dist/components/modules/ProductDetailAddToCart.vue';
import { useProductDetailPageStore } from './productDetailPageStore';
import { useProductAddonsStore } from './productAddonsStore';
import { createDiscountPercentageText } from './helper/createDiscountPercentageText';
import { withReset } from '~/utils/pinia/withReset';
import { useCrossSellingProductsStore } from '~/stores/productDetailPageStore/crossSellingProductsStore';
import { useAccessoryAssistantStore } from '~/stores/productDetailPageStore/accessoryAssistantStore';

/**
 * This store is responsible for the displayed price information
 * on the product detail page.
 */
export const useProductPriceStore = defineStore('product-price-store', () => {
  const _state = {};
  const showPrice = computed<boolean>(() => {
    return useProductDetailPageStore().decoratedProduct?.showPrice ?? false;
  });

  const priceText = computed<string>(() => {
    const productStore = useProductDetailPageStore();
    const productAddonStore = useProductAddonsStore();
    const crossSellingProductsStore = useCrossSellingProductsStore();
    const accessoryAssistantStore = useAccessoryAssistantStore();

    const displayPrice =
      productStore.decoratedProduct?.discountPrice ??
      productStore.decoratedProduct?.price ??
      0;

    return (
      productStore.decoratedProduct?.createPriceText(
        displayPrice +
          productAddonStore.selectedItemsPrice +
          crossSellingProductsStore.selectedCrossSellingProductsPrice +
          accessoryAssistantStore.selectedDecoratedProductPrice
      ) ?? ''
    );
  });

  /**
   * Returns the discount percentage as readable text for the current
   * product including all selected addons. If no addons, no discount
   * or price of the product are given, `undefined` is returned.
   * This should actually be done by magento, see https://gcp.baslerweb.com/jira/browse/DBP-795.
   * @returns The percentage text, e.g. `"-50%"`
   * @see https://gcp.baslerweb.com/jira/browse/DBP-750
   */
  const discountPercentageText = computed<string | undefined>(() => {
    const productStore = useProductDetailPageStore();
    const productAddonStore = useProductAddonsStore();

    return createDiscountPercentageText(
      productStore.decoratedProduct,
      productAddonStore.selectedItemsPrice
    );
  });

  /**
   * Returns the base product price text plus all (base) addon prices.
   * This calculation should be performed by magento, see
   * https://gcp.baslerweb.com/jira/browse/DBP-795.
   * @see https://gcp.baslerweb.com/jira/browse/DBP-750
   */
  const defaultProductPriceWithAddons = computed<string | undefined>(() => {
    const productStore = useProductDetailPageStore();
    const productAddonStore = useProductAddonsStore();
    const crossSellingProductsStore = useCrossSellingProductsStore();
    const accessoryAssistantStore = useAccessoryAssistantStore();

    const selectedItemsPrice = productAddonStore.selectedItemsPrice;
    const selectedCrossSellingProductsPrice =
      crossSellingProductsStore.selectedCrossSellingProductsPrice;
    const selectedAccessoryAssistantDecoratedProductPrice =
      accessoryAssistantStore.selectedDecoratedProductPrice;

    const decoratedProduct = productStore.decoratedProduct;

    if (
      !decoratedProduct ||
      selectedItemsPrice == 0 ||
      !decoratedProduct.price
    ) {
      return;
    }

    return decoratedProduct.createPriceText(
      decoratedProduct.price +
        selectedItemsPrice +
        selectedCrossSellingProductsPrice +
        selectedAccessoryAssistantDecoratedProductPrice
    );
  });

  const addToCartAttribute = computed<AddToCartAttribute>(() => {
    const { $textDictionary } = useNuxtApp();
    const productStore = useProductDetailPageStore();

    const previousValue =
      productStore.decoratedProduct?.showPrice &&
      productStore.decoratedProduct?.isSale
        ? defaultProductPriceWithAddons.value ??
          productStore.decoratedProduct?.previousPriceText
        : undefined;
    const previousValuePostfix =
      productStore.decoratedProduct?.showPrice &&
      productStore.decoratedProduct?.isSale
        ? discountPercentageText.value ??
          productStore.decoratedProduct?.pricePrefixAttentionText
        : undefined;

    // XXX: price always present? Define what to display if price is not set.
    // define in new ticket regarding price.
    if (showPrice.value) {
      return {
        type: 'attribute',
        label: $textDictionary.value['pdp.netPrice.label']?.replace(
          /(<([^>]+)>)/gi,
          ''
        ),
        size: 'xlarge',
        layout: undefined,
        value: priceText.value,
        previousValue,
        previousValuePostfix
      } satisfies AddToCartAttribute;
    }

    return {
      type: 'attribute',
      label: $textDictionary.value['pdp.netPrice.label']?.replace(
        /(<([^>]+)>)/gi,
        ''
      ),
      size: 'xlarge',
      layout: undefined,
      value: priceText.value,
      previousValue,
      previousValuePostfix
    } satisfies AddToCartAttribute;
  });

  const isRequestButtonDisabled = computed<boolean>(() => {
    const productStore = useProductDetailPageStore();
    const productAddonStore = useProductAddonsStore();

    return (
      !productAddonStore.isRequiredOptionsSelected ||
      (productStore.isConfigurableProduct && !productStore.variant)
    );
  });

  const isAddToCartButtonDisabled = computed<boolean>(() => {
    const productStore = useProductDetailPageStore();
    const productAddonStore = useProductAddonsStore();

    return (
      !productAddonStore.isRequiredOptionsSelected ||
      (productStore.isConfigurableProduct && !productStore.variant)
    );
  });

  return withReset({
    _state,
    showPrice,
    priceText,
    discountPercentageText,
    defaultProductPriceWithAddons,
    addToCartAttribute,
    isRequestButtonDisabled,
    isAddToCartButtonDisabled
  });
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useProductPriceStore, import.meta.hot)
  );
}
